import { c as _c } from "react/compiler-runtime";
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCombobox } from '@mantine/core';
import { useDynamicCallback } from 'components/hooks/useDynamicCallback';
import { useCallback, useState, useEffect, useLayoutEffect } from 'react';
import { match } from 'ts-pattern';
import { SelectionOffset, getSelectionOffset, getTextUpToAnchor } from './utils';
import { $getSelection, $isRangeSelection, RangeSelection } from 'lexical';
import { doTo } from 'shared/promise';
export const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
export const makeTypeaheadTriggerMatch = (trigger: string, {
  minLength = 1,
  maxLength = 75
}: {
  minLength?: number;
  maxLength?: number;
}) => (text: string) => {
  const TypeaheadTriggerRegex = new RegExp(`(^|\\s|\\()([${trigger}]((?:[^${trigger + PUNCTUATION}\\s]){0,${maxLength}}))$`);
  const match = TypeaheadTriggerRegex.exec(text);
  if (match == null) return;
  const maybeLeadingWhitespace = match[1]!;
  const matchingString = match[3]!;
  if ((matchingString?.length ?? 0) < minLength) return;
  return {
    leadOffset: match.index + (maybeLeadingWhitespace?.length ?? 0),
    matchingString,
    replaceableString: match[2]!
  };
};
export const useTypeaheadTriggerMatch = (trigger: string, options: {
  minLength?: number;
  maxLength?: number;
}) => useCallback(makeTypeaheadTriggerMatch(trigger, options), [options.minLength, options.maxLength, trigger]);
export type QueryMatch = Exclude<ReturnType<ReturnType<typeof useTypeaheadTriggerMatch>>, undefined>;
export const useTriggerMatchedCombo = t0 => {
  const $ = _c(33);
  const {
    trigger
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  if ($[0] !== editor) {
    t1 = editor.isEditable();
    $[0] = editor;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const readonly = !t1;
  const [selectionOffset, setSelectionOffset] = useState(undefined);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      minLength: 0
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const checkForTriggerMatch = useTypeaheadTriggerMatch(trigger, t2);
  const [triggerMatch, setTriggerMatch] = useState();
  const t3 = triggerMatch != null;
  let t4;
  if ($[3] !== t3) {
    t4 = {
      opened: t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const combo = useCombobox(t4);
  let t5;
  if ($[5] !== checkForTriggerMatch || $[6] !== editor || $[7] !== readonly) {
    t5 = () => {
      if (readonly) {
        return;
      }
      return editor.registerUpdateListener(state => {
        editor.getEditorState().read(() => {
          const offset = getSelectionOffset(editor);
          setSelectionOffset(offset);
          const selection = $getSelection();
          const isRange = $isRangeSelection(selection);
          const isDirty = state.dirtyLeaves.size > 0;
          const match$0 = isDirty && isRange ? checkForTriggerMatch(getTextUpToAnchor(selection) ?? "") : undefined;
          setTriggerMatch(match$0 && isRange && selection ? {
            match: match$0,
            selection
          } : undefined);
        });
      });
    };
    $[5] = checkForTriggerMatch;
    $[6] = editor;
    $[7] = readonly;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== editor || $[10] !== readonly) {
    t6 = [editor, readonly];
    $[9] = editor;
    $[10] = readonly;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== combo || $[13] !== readonly) {
    t7 = e => {
      if (readonly || !combo.dropdownOpened) {
        return;
      }
      match(e.key).with("ArrowUp", "ArrowDown", "Enter", k => {
        e.preventDefault();
        e.stopPropagation();
        match(k).with("ArrowUp", () => combo.selectPreviousOption()).with("ArrowDown", () => combo.selectNextOption()).with("Enter", () => combo.clickSelectedOption()).exhaustive();
      }).with("Escape", () => {
        combo.closeDropdown();
      }).otherwise(_temp);
    };
    $[12] = combo;
    $[13] = readonly;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleKeyDownDynamic = useDynamicCallback(t7);
  let t8;
  if ($[15] !== editor || $[16] !== handleKeyDownDynamic || $[17] !== readonly) {
    t8 = () => {
      if (readonly) {
        return;
      }
      return editor.registerRootListener((root, prev) => {
        if (root) {
          root.addEventListener("keydown", e_0 => handleKeyDownDynamic(e_0), {
            capture: true,
            passive: false
          });
        }
        if (prev) {
          prev.removeEventListener("keydown", handleKeyDownDynamic);
        }
      });
    };
    $[15] = editor;
    $[16] = handleKeyDownDynamic;
    $[17] = readonly;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== editor || $[20] !== readonly) {
    t9 = [editor, readonly];
    $[19] = editor;
    $[20] = readonly;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  useLayoutEffect(t8, t9);
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setTriggerMatch(null);
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== selectionOffset) {
    t11 = selectionOffset?.left != null ? doTo(selectionOffset, _temp2) : {
      display: "none"
    };
    $[23] = selectionOffset;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = {
      className: "absolute",
      style: {
        ...t11
      }
    };
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== combo || $[28] !== editor || $[29] !== readonly || $[30] !== t12 || $[31] !== triggerMatch) {
    t13 = {
      editor,
      readonly,
      store: combo,
      triggerMatch,
      reset: t10,
      targetDivProps: t12
    };
    $[27] = combo;
    $[28] = editor;
    $[29] = readonly;
    $[30] = t12;
    $[31] = triggerMatch;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
};

/**
 * Split Lexical TextNode and return a new TextNode only containing matched text.
 * Common use cases include: removing the node, replacing with a new node.
 */
export const splitNodeContainingQuery = (match: QueryMatch) => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
  const anchor = selection.anchor;
  if (anchor.type !== 'text') return null;
  const anchorNode = anchor.getNode();
  if (!anchorNode.isSimpleText()) return null;
  const selectionOffset = anchor.offset;
  const textContent = anchorNode.getTextContent().slice(0, selectionOffset);
  const characterOffset = match.replaceableString.length;
  const queryOffset = getFullMatchOffset(textContent, match.matchingString, characterOffset);
  const startOffset = selectionOffset - queryOffset;
  if (startOffset < 0) {
    return null;
  }
  let newNode;
  if (startOffset === 0) {
    ;
    [newNode] = anchorNode.splitText(selectionOffset);
  } else {
    ;
    [, newNode] = anchorNode.splitText(startOffset, selectionOffset);
  }
  return newNode;
};

/**
 * Walk backwards along user input and forward through entity title to try
 * and replace more of the user's text with entity.
 */
const getFullMatchOffset = (documentText: string, entryText: string, offset: number) => {
  let triggerOffset = offset;
  for (let i = triggerOffset; i <= entryText.length; i++) {
    if (documentText.substring(-i) === entryText.substring(0, i)) triggerOffset = i;
  }
  return triggerOffset;
};
function _temp() {}
function _temp2(t0) {
  const {
    left,
    top
  } = t0;
  return {
    left,
    top: top - 12
  };
}